import { FetchParams } from "@kapex/tables";
import { openModal } from "@mantine/modals";
import { createContext, useContext, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import DateRange from "src/modules/Dashboard/DateRange";
import { defaultPagination } from "src/utils/constants";
import { getDateRanges } from "src/utils/dateHelper";

interface DashboardContextType {
  filters: IChartFilter;
  setFilters: (e: IChartFilter) => void;
  filtersQuick: IChartFilter;
  setFiltersQuick: (e: IChartFilter) => void;
  getDateRangeText: (e: IChartFilter) => void;
  handleModal: (bool?: boolean) => void;
  handleLocChange: (locations: string[]) => void;
  paginationParams: Partial<FetchParams>;
  setPaginationParams: (e: Partial<FetchParams>) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined
);

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardContext must be used within a DashboardContextProvider"
    );
  }
  return context;
};

export type IChartFilter = {
  locations: string[];
  dateRange?: [Date, Date];
};

const defaultFilters = {
  locations: [],
  dateRange: [
    new Date(new Date().setHours(0, 0, 0, 0)),
    new Date(new Date().setHours(23, 59, 59, 999)),
  ] as [Date, Date],
};

const defaultFiltersQuick = {
  locations: [],
  dateRange: getDateRanges().thisMonth as [Date, Date],
};

export const DashboardContextProvider = () => {
  const [filters, setFilters] = useState<IChartFilter>(defaultFilters);
  const [filtersQuick, setFiltersQuick] =
    useState<IChartFilter>(defaultFiltersQuick);
  const [paginationParams, setPaginationParams] =
    useState<Partial<FetchParams>>(defaultPagination);

  const handleLocChange = (locations: string[]) => {
    setFilters({ ...filters, locations });
  };

  const handleModal = useCallback(
    (isQuickFilter = false) => {
      openModal({
        closeOnClickOutside: false,
        title: "Date Range",
        children: (
          <DateRange
            filters={isQuickFilter ? filtersQuick : filters}
            setFilters={isQuickFilter ? setFiltersQuick : setFilters}
          />
        ),
      });
    },
    [filters, filtersQuick]
  );

  const getDateRangeText = (Filters: IChartFilter) => {
    if (Filters.dateRange) {
      const startDate = Filters.dateRange[0].toLocaleDateString();
      const endDate = Filters.dateRange[1].toLocaleDateString();
      if (startDate === endDate) {
        return Filters.dateRange[0].toLocaleDateString();
      } else {
        return (
          Filters.dateRange[0].toLocaleDateString() +
          " - " +
          Filters.dateRange[1].toLocaleDateString()
        );
      }
    }
    return "Date Range";
  };

  return (
    <DashboardContext.Provider
      value={{
        filters,
        setFilters,
        getDateRangeText,
        handleModal,
        handleLocChange,
        paginationParams,
        setPaginationParams,
        filtersQuick,
        setFiltersQuick,
      }}
    >
      <Outlet />
    </DashboardContext.Provider>
  );
};
